



























































































































































































import { defineComponent, ref, computed } from '@vue/composition-api';
import { SwapAsset } from '@nimiq/fastspot-api';
import { GearIcon, Tooltip, InfoCircleIcon } from '@nimiq/vue-components';
import { RouteName, useRouter } from '@/router';
import AnnouncementBox from '../AnnouncementBox.vue';
import AccountMenu from '../AccountMenu.vue';
import PriceChart, { TimeRange } from '../PriceChart.vue';
import BalanceDistribution from '../BalanceDistribution.vue';
import ConsensusIcon from '../ConsensusIcon.vue';
import StreetconeIcon from '../icons/StreetconeIcon.vue';
import AttentionDot from '../AttentionDot.vue';

import { useAddressStore } from '../../stores/Address';
import { useBtcAddressStore } from '../../stores/BtcAddress';
import { usePolygonAddressStore } from '../../stores/PolygonAddress';
import { useSettingsStore } from '../../stores/Settings';
import { useAccountStore, AccountType } from '../../stores/Account';
import { useAccountSettingsStore } from '../../stores/AccountSettings';
import { useSwapsStore } from '../../stores/Swaps';
import { useConfig } from '../../composables/useConfig';
import { useWindowSize } from '../../composables/useWindowSize';
import { i18n } from '../../i18n/i18n-setup';
import { ENV_TEST, ENV_DEV, CryptoCurrency, FIAT_CURRENCIES_OFFERED } from '../../lib/Constants';

enum SellProvider {
    Simplex = 'simplex',
    Moonpay = 'moonpay',
    Coinify = 'coinify',
}

export default defineComponent({
    props: {},
    setup(props, context) {
        const router = useRouter();
        const { config } = useConfig();
        const { isMobile, height: windowHeight } = useWindowSize();

        async function navigateTo(path: string | { name: string }) {
            if (isMobile.value) {
                return router.replace(path);
            }
            return router.push(path).catch(() => { /* ignore */ });
        }

        async function openModal(routeName: RouteName, params: Record<string, string> = {}) {
            // Try to find a context-specific variant of the modal for Settings or Network pages.
            // For example, if we're on Settings and trying to open 'buy', try 'settings-buy' first.
            let targetRouteName = routeName;
            const currentRouteName = router.currentRoute.name;

            if (currentRouteName === RouteName.Settings || currentRouteName === RouteName.Network) {
                const prefix = currentRouteName === RouteName.Settings ? 'settings' : 'network';
                const contextRouteName = `${prefix}-${routeName}` as RouteName;

                // Check if this context-specific route exists
                try {
                    const contextRoute = router.resolve({ name: contextRouteName });
                    if (contextRoute && contextRoute.route.matched.length > 0) {
                        targetRouteName = contextRouteName;
                    }
                } catch (e) {
                    // Route doesn't exist, fall back to original
                }
            }

            // Each modal is expected to be sitting above a specific parent route / background page. If we're not
            // currently on that route, navigate to it first, such that the modal can be closed later by a simple back
            // navigation leading to that parent route. If we wouldn't do that, a back navigation would lead back to our
            // current route, but with the modal still open on top.
            const modalRoute = router.resolve({ name: targetRouteName }).route;
            const expectedParentRoute = modalRoute.matched.find(({ name }) => !!name && name !== targetRouteName);
            if (expectedParentRoute && router.currentRoute.name !== expectedParentRoute.name) {
                // Don't keep the sidebar open for this navigation on mobile because closing it would be a back
                // navigation on the parent page, leading back to the route we're currently on, instead of closing the
                // sidebar.
                await navigateTo({ name: expectedParentRoute.name! });
            }
            return router.push({
                name: targetRouteName,
                query: { sidebar: 'true' }, // on mobile keep sidebar open in background
                params,
            }).catch(() => { /* ignore */ });
        }

        // Note: config.environment should never change at runtime.
        const isTestnet = config.environment === ENV_TEST || config.environment === ENV_DEV;
        const isDev = config.environment === ENV_DEV;

        const priceChartTimeRange = ref(TimeRange['24h']);
        function switchPriceChartTimeRange() {
            if (priceChartTimeRange.value === TimeRange['24h']) {
                priceChartTimeRange.value = TimeRange['7d'];
            } else {
                priceChartTimeRange.value = TimeRange['24h'];
            }
        }

        const { addressInfos, selectAddress } = useAddressStore();
        function resetState() {
            navigateTo('/');

            if (addressInfos.value.length > 0) {
                selectAddress(addressInfos.value[0].address);
            }
        }

        const { updateAvailable, canUseSwaps } = useSettingsStore();

        const { activeAccountInfo, hasBitcoinAddresses, hasPolygonAddresses } = useAccountStore();
        const isLegacyAccount = computed(() => activeAccountInfo.value?.type === AccountType.LEGACY);

        const walletActivatedCurrencies = computed(() => [
            CryptoCurrency.NIM, // NIM is always activated
            ...(config.enableBitcoin && hasBitcoinAddresses.value ? [CryptoCurrency.BTC] : []),
            ...(config.polygon.enabled && hasPolygonAddresses.value ? [CryptoCurrency.USDC] : []),
        ]);

        const fastspotEnabledFiatSwapAssets = computed(() => config.fastspot.enabledSwapAssets
            .filter((asset) => (FIAT_CURRENCIES_OFFERED as string[]).includes(asset.toLowerCase())));
        const fastspotEnabledCryptoSwapAssets = computed(() => config.fastspot.enabledSwapAssets
            .filter((asset) => !fastspotEnabledFiatSwapAssets.value.includes(asset)
                && (config.enableBitcoin || asset !== SwapAsset.BTC)
                && (config.polygon.enabled || (asset !== SwapAsset.USDC_MATIC && asset !== SwapAsset.USDC)),
            ),
        );

        const { stablecoin } = useAccountSettingsStore();
        const hasSwappableBalance = computed(() => [useAddressStore, useBtcAddressStore, usePolygonAddressStore]
            .some((useStore) => {
                const store = useStore();
                return 'accountUsdcBalance' in store
                    ? stablecoin.value === CryptoCurrency.USDC
                        // For USDC, only native USDC is supported for swapping.
                        ? store.accountUsdcBalance.value
                        : stablecoin.value === CryptoCurrency.USDT
                            ? store.accountUsdtBridgedBalance.value
                            : 0
                    : store.accountBalance.value;
            }),
        );

        const { activeSwap } = useSwapsStore();
        const hasActiveSwap = computed(() => activeSwap.value !== null);

        // The Sidebar's buttons have mousedown.prevent to circumvent focus issues on iOS. As this also causes focused
        // tooltips to not lose focus and close if such a button is clicked, we close them manually.
        function hideTooltips() {
            for (const refComponent of Object.values(context.refs)) {
                if (!(refComponent instanceof Tooltip)) continue;
                refComponent.hide();
            }
        }

        const { activeCurrency } = useAccountStore();

        const canSellCryptoWithMoonpay = computed(() => { // eslint-disable-line arrow-body-style
            if (!config.moonpay.enabled) return false;
            return activeCurrency.value !== CryptoCurrency.NIM;
        });

        const enabledSellProviders = computed(() => {
            const providers: SellProvider[] = [];

            if (canSellCryptoWithMoonpay.value) providers.push(SellProvider.Moonpay);

            // Coinify can sell all wallet-supported cryptocurrencies
            if (config.coinify.enabled) providers.push(SellProvider.Coinify);

            return providers;
        });

        const sellEnabled = computed(() => enabledSellProviders.value.length > 0);

        const sellModals = {
            [SellProvider.Moonpay]: RouteName.MoonpaySellInfo,
            [SellProvider.Coinify]: RouteName.CoinifySellInfo,
            [SellProvider.Simplex]: RouteName.SellCrypto, // This is a fallback, should never be reached
        };

        function openSellModal() {
            if (enabledSellProviders.value.length === 0) return;
            const modalName: RouteName = enabledSellProviders.value.length === 1
                ? sellModals[enabledSellProviders.value[0]]
                : RouteName.SellCrypto; // SellCrypto is for SEPA operations. We need to add a new Sell Selector.
            openModal(modalName);
        }

        const nimSellOptions = computed(() => {
            const [intro, optionSwap, optionExchange] = (i18n.t('You can sell NIM\n'
                + '- in the Wallet by swapping NIM to BTC or USDC first, which can then be sold.\n'
                + '- on supported exchanges.') as string).split(/\s*-\s*/);
            // return options only if translators translated them correctly and kept the format
            return intro && optionSwap && optionExchange
                ? { intro, optionSwap, optionExchange }
                : null;
        });

        return {
            CryptoCurrency,
            navigateTo,
            resetState,
            isTestnet,
            isDev,
            windowHeight,
            priceChartTimeRange,
            switchPriceChartTimeRange,
            isLegacyAccount,
            walletActivatedCurrencies,
            fastspotEnabledFiatSwapAssets,
            fastspotEnabledCryptoSwapAssets,
            hasSwappableBalance,
            hasActiveSwap,
            canUseSwaps,
            updateAvailable,
            hideTooltips,
            openModal,
            RouteName,
            SellProvider,
            enabledSellProviders,
            sellEnabled,
            openSellModal,
            nimSellOptions,
            activeCurrency,
        };
    },
    components: {
        AnnouncementBox,
        GearIcon,
        PriceChart,
        BalanceDistribution,
        AccountMenu,
        ConsensusIcon,
        Tooltip,
        InfoCircleIcon,
        StreetconeIcon,
        AttentionDot,
    },
});
